<template>
  <section class="dialog-wrapper">
    <div class="img-wrapper">
      <img :src="img" :alt="name" />
    </div>
    <h3 class="heading">Choose Dates</h3>
    <p class="main-hint">Select Dates that fit before, during or after your choosen destinations</p>
    <ul class="available-trips">
      <li class="trip-item departure">
        <div class="image-holder departure">
          <img :src="defaultFlightIcon" alt="Departure" />
        </div>
        <div class="main-description">
          <h4>Departure</h4>
          <p class="trip-description">
            {{ departureDate }}
          </p>
        </div>
        <div class="actions">
          <date-range-picker
            class="date-picker-base"
            opens="left"
            :locale-data="{firstDay: 1, format: 'dd-mmm-yyyy'}"
            :showDropdowns="true"
            :dateRange="getDepartureDate()"
            @update="onTripDateUpdate($event)"
            :singleDatePicker="true"
            :appendToBody="true"
            :ranges="false"
            :showWeekNumbers="false"
            :dateFormat="dateFormat"
            :autoApply="true"
            :calculatePosition="calculatePosition"
            controlContainerClass="report-input-wrapper"
            ref="picker"
          >
            <template v-slot:input style="border: none; padding: 0">
              <button class="core-btn outline">
                <CalendarIcon class="plus-icon calendar" />
                Edit Departure
              </button>
            </template>
          </date-range-picker>
        </div>
      </li>
      <li v-for="trip in availbaleTrips" :key="trip.id" class="trip-item">
        <div class="image-holder">
          <img :src="trip.img" :alt="trip.name" />
        </div>
        <div class="main-description">
          <h4>{{ trip.name }}</h4>
          <p class="trip-description">
            {{ trip.description }}
          </p>
        </div>
        <div class="actions">
          <date-range-picker
            class="date-picker-base"
            opens="left"
            :minDate="getDepartureDate().startDate"
            :locale-data="{firstDay: 1, format: 'dd-mmm-yyyy'}"
            :showDropdowns="true"
            :dateRange="getTripDate(trip)"
            @update="onTripDateUpdate($event, trip.id)"
            :singleDatePicker="true"
            :appendToBody="true"
            :ranges="false"
            :showWeekNumbers="false"
            :dateFormat="dateFormat"
            :autoApply="true"
            :calculatePosition="calculatePosition"
            controlContainerClass="report-input-wrapper"
            ref="picker"
          >
            <template v-slot:input style="border: none; padding: 0">
              <button class="core-btn outline" v-if="trip.isDateAvailable">
                <CalendarIcon class="plus-icon calendar" />
                Edit Departure
              </button>
              <button class="core-btn outline" v-else>
                <span class="plus-icon">+</span>
                Add Departure
              </button>
            </template>
          </date-range-picker>
        </div>
      </li>
    </ul>

    <div class="continue-btn">
      <button class="core-button" :disabled="isSaveDisabled" @click="submit">Save</button>
    </div>
  </section>
</template>

<script>
import Vue from 'vue';
import VueCompositionAPI, {computed, defineComponent, ref} from '@vue/composition-api';
import {getReadableDuration} from '@mobi-components/amex-bluesky-components';
import defaultFlightIcon from '@components/svg/brief-case.svg';
import DateRangePicker from '@mobi-components/vue2-daterange-picker';
import '@mobi-components/vue2-daterange-picker/dist/vue2-daterange-picker.css';
import CalendarIcon from '@components/svg/calendar.vue';

Vue.use(VueCompositionAPI);

export default defineComponent({
  name: 'select-dates-form',
  components: {
    DateRangePicker,
    CalendarIcon,
  },
  props: {
    tripsToSchedule: {
      type: Object,
      default: null,
    },
    img: {
      type: String,
      default: '',
    },
    onDatesSelected: {
      type: Function,
      default: () => () => Promise.resolve(),
    },
  },
  setup(props, {emit}) {
    const tripDates = ref({});

    const getDepartureDate = () => {
      const date = tripDates.value['departure'];
      const departure = props.tripsToSchedule.reduce((min, x) => {
        const newMin = x.dateRange?.startDate.valueOf();
        if (isNaN(newMin)) {
          return min;
        }
        if (!min || newMin < min) {
          return newMin;
        }
        return min;
      }, null);
      return (
        date || {
          startDate: new Date(departure),
          endDate: new Date(departure),
        }
      );
    };

    const departureDate = computed(() => {
      const dateRange = getDepartureDate();
      const readable = getReadableDuration(dateRange.startDate, dateRange.startDate);
      return readable ? `Departing ${readable}` : '';
    });

    const getTripDate = (trip) => {
      const dateRange = tripDates.value[trip.id];
      const tripDatRange = trip.dateRange
        ? {
            startDate: trip.dateRange.endDate,
            endDate: trip.dateRange.endDate,
          }
        : null;
      return (
        dateRange ||
        (trip.dateRange && tripDatRange) || {
          startDate: null,
          endDate: null,
        }
      );
    };

    const availbaleTrips = computed(() => {
      return (props.tripsToSchedule || [])
        .sort((a, b) => {
          const aStart = getTripDate(a).endDate || Number.MAX_VALUE;
          const bStart = getTripDate(b).endDate || Number.MAX_VALUE;
          return aStart - bStart;
        })
        .map((x) => {
          const date = getTripDate(x);
          return {
            ...x,
            name: (x.name || '').replace('_', ', '),
            img: x.img,
            description: `${date?.endDate ? getReadableDuration(date.endDate, date.endDate) : ''}`,
            isDateAvailable: !!date?.endDate,
          };
        });
    });

    const dateFormat = (classes, date) => {
      if (!classes.disabled) {
        classes.disabled = date.getTime() < new Date();
      }
      return classes;
    };

    const getSelectedDates = () => {
      const dates = availbaleTrips.value.reduce(
        (res, x) => {
          res[x.id] = getTripDate(x).endDate;
          return res;
        },
        {departure: getDepartureDate().startDate},
      );
      return dates;
    };

    const onTripDateUpdate = (range, id) => {
      const updatedId = id || 'departure';
      tripDates.value = {
        ...tripDates.value,
        [updatedId]: range,
      };

      if (!isSaveDisabled.value) {
        const onDatesSelectedCall = props.onDatesSelected || (() => Promise.resolve());
        onDatesSelectedCall(getSelectedDates());
      }
    };

    const calculatePosition = (dropdownList, _component, {top, left /*, width, right */}) => {
      dropdownList.style.right = 'auto';
      dropdownList.style.top = top - dropdownList.clientHeight + 'px';
      dropdownList.style.left = left + 'px';
    };

    const isSaveDisabled = computed(() => {
      return availbaleTrips.value.some((x) => !x.isDateAvailable);
    });

    const submit = () => {
      const dates = getSelectedDates();
      emit('dates-selected', dates);
    };

    return {
      availbaleTrips,
      departureDate,
      defaultFlightIcon,
      dateFormat,
      getTripDate,
      onTripDateUpdate,
      calculatePosition,
      getDepartureDate,
      isSaveDisabled,
      submit,
    };
  },
});
</script>

<style scoped>
.report-input-wrapper {
  border: none;
  padding: 0;
}

.date-picker-base {
  width: 100%;
  /* padding: 5px 10px 5px 22px; */
  z-index: 1;
}

.dialog-wrapper {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.img-wrapper {
  width: 50%;
  margin: auto;
}

.img-wrapper > img {
  width: 100%;
  border-radius: 10px;
}

.heading {
  font-style: normal;
  font-weight: 700;
  font-size: 39px;
  line-height: 98.1%;
  text-align: center;
  color: #0c1e39;
}

.main-hint {
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 98.1%;
  text-align: center;
  color: #858e9c;
  max-width: 60%;
  margin: auto;
}

.available-trips {
  overflow: auto;
  flex: 1;
  padding: 0;
  margin: 30px 0 0 0;
  width: 100%;
}

.trip-item {
  list-style: none;
  display: flex;
  margin: 10px;
}

.trip-item.departure {
  border-top: 1px solid #f1f1f1;
  border-bottom: 1px solid #f1f1f1;
  padding: 14px 0;
}

.image-holder {
  width: 56px;
  height: 56px;
  border-radius: 5px;

  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  background-size: cover;
}

.image-holder img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-holder.departure {
  border: 2px solid #f1f1f1;
}

.image-holder.departure img {
  width: 60%;
  height: 60%;
  min-width: 0;
  min-height: 0;
}

.main-description {
  margin: 10px;
  flex: 1;
}

.main-description > h4 {
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 98.1%;
  color: #0c1e39;
  margin: 0;
}

.main-description > p {
  margin: 3px 0 0 0;
  padding: 0 !important;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 98.1%;

  color: #858e9c;
}

.core-btn {
  --core-btn-bg-color: var(--btn-primary-color, hsl(208, 100%, 41%));
  --core-btn-border: var(--btn-primary-color, hsl(208, 100%, 41%));
  --core-btn-color: #fff;
  height: 40px;
  line-height: 41px;
  color: var(--core-btn-color);
  text-decoration: none !important;
  border-radius: 5px;
  padding: 0 10px;
  background-color: var(--core-btn-bg-color);
  border: 1px solid var(--core-btn-border);
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;
  display: flex;
  align-items: center;

  font-style: normal;
  font-weight: 700;
  font-size: 12px;

  opacity: 0.9;
}

.core-btn.outline {
  --core-btn-bg-color: transparent;
  --core-btn-color: var(--core-btn-border);
}

.plus-icon {
  display: inline-block;
  margin-right: 5px;
  position: relative;
  top: -2px;
}

.plus-icon.calendar {
  top: 0;
}

.loading {
  min-height: 100px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.continue-btn {
  margin-top: 30px;
  display: flex;
  justify-content: center;
}
</style>
